import { Stack, Text, Title, useMantineTheme } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { mock } from '@wagmi/core'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import RootButton from '@/components/Buttons/RootButton'
import { STUDIO_BOT } from '@/constants/blockchain'
import { useGovernanceContext } from '@/contexts/GovernanceContext'
import useProfilePath from '@/hooks/useProfilePath'
import useSpacesAuth from '@/hooks/useSpacesAuth'
import { useAuth } from '@/plugins/auth'
import { BodyStack } from '@/styled/components'
import { fetchIpfsMetadata, post } from '@/utils/utils'

import { ConfirmationReady } from '../Identity/ConfirmModals/utils'
import ActionNomination from '../Modals/Governance/ActionNomination'
import ModalSkeleton from '../Modals/ModalSkeleton'

const ERROR_MESSAGES = {
  NO_ADDRESS: 'Please connect your wallet',
  NO_TOKEN: 'Please sign message to submit an application',
  NO_FORM_VALID: 'Please fill out all fields',
}

interface FormQuestion {
  question: string
  subQuestion?: string
}
// Define Application form and Questions
export interface ApplicationFormData {
  vision: string
  contribution: string
  experience: string
  running: string
}

export interface ApplicationFormQuestions {
  vision: FormQuestion
  contribution: FormQuestion
  experience: FormQuestion
  running: FormQuestion
}

export const sectionQuestions: ApplicationFormQuestions = {
  vision: {
    question: 'What is your vision for the OMMA Council?',
  },
  contribution: {
    question: 'How do you plan to contribute to the community?',
  },
  experience: {
    question: 'What relevant experience do you bring to this role?',
  },
  running: {
    question:
      'Are you running as an individual candidate or a contract (as DAO, collective organization)?',
    subQuestion: `
    If you're running as a contract, please include a link to the source code of your contract (ex: git, verified contract).`,
  },
}

const Component = () => {
  const theme = useMantineTheme()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { address } = useAuth()
  const { token, fetchAuthToken } = useSpacesAuth()
  const { isNominee, hasBeenNominated, nomineeApplication, electionData } = useGovernanceContext()
  const nomineeCid = useMemo(() => nomineeApplication(address || ''), [nomineeApplication, address])
  const profilePath = useProfilePath(address || '')

  const [formData, setFormData] = useState<ApplicationFormData>({
    vision: '',
    contribution: '',
    experience: '',
    running: '',
  })

  const [opened, { open, close }] = useDisclosure()

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')

  const isFormValid = useCallback(
    () => Object.values(formData).every(value => value.length > 0 && value.length <= 1000),
    [formData]
  )

  // Voting period started, so users cannot fill applications anymore or edit
  const fillApplicationPeriodEnded = useMemo(() => {
    const now = new Date().getTime() / 1000
    return electionData.campaignTime < now
  }, [electionData])

  // Redirect if not a nominee
  useEffect(() => {
    if ((address && !hasBeenNominated(address) && !mock) || fillApplicationPeriodEnded)
      navigate('/elections')
  }, [hasBeenNominated, address, navigate, fillApplicationPeriodEnded])

  // Show error if no address or token
  useEffect(() => {
    if (!address) {
      setError(ERROR_MESSAGES.NO_ADDRESS)
    } else if (!token) {
      setError(ERROR_MESSAGES.NO_TOKEN)
    } else if (!isFormValid()) {
      setError(ERROR_MESSAGES.NO_FORM_VALID)
    } else {
      setError('')
    }
  }, [address, token, isFormValid])

  // get content from nomineeCid
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const content = await fetchIpfsMetadata(`ipfs://${nomineeCid}`)
        setFormData(content)
        setError('')
      } catch (err: any) {
        console.log('Error fetching metadata', error)
      }
    }
    fetchContent()
  }, [nomineeCid])

  const handleInputChange = (field: keyof ApplicationFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }))
    setError('')
  }

  const handleSubmit = useCallback(async () => {
    if (!address) {
      setError(ERROR_MESSAGES.NO_ADDRESS)
      close()
      return
    }

    if (!token) {
      setError(ERROR_MESSAGES.NO_TOKEN)
      close()
      return
    }

    if (!isFormValid()) {
      setError(ERROR_MESSAGES.NO_FORM_VALID)
      close()
      return
    }

    setIsSubmitting(true)
    setError('')

    try {
      const payload = {
        address,
        application: JSON.stringify(formData),
      }

      const response = await post(
        `${STUDIO_BOT}/elections/application`,
        JSON.stringify(payload),
        token
      )

      if (response?.valid) {
        // CID is updated onchain by spaces server
      } else {
        setError('Failed to submit application')
        close()
      }
    } catch (err) {
      setError('An error occurred while submitting your application')
      close()
    } finally {
      setIsSubmitting(false)
      setSubmitted(true)
    }
  }, [address, token, isFormValid, formData, close])

  const handleSubmitButton = useCallback(() => {
    if (!address) return
    if (isNominee(address)) {
      handleSubmit()
    } else open()
  }, [open, address, isNominee, handleSubmit])

  return (
    <BodyStack>
      <Stack>
        <Title order={2}>
          {t('components.governance.application.title', 'Council Application')}
        </Title>
        <Text>
          {t(
            'components.governance.application.description',
            'To accept the nomination, please fill out the following application form.'
          )}
        </Text>
      </Stack>
      {Object.entries(sectionQuestions).map(([key, { question, subQuestion }]) => (
        <Stack key={key} gap="sm">
          <Title order={5}>{question}</Title>
          {subQuestion && <Text>{subQuestion}</Text>}
          <textarea
            value={formData[key as keyof ApplicationFormData]}
            onChange={e => handleInputChange(key as keyof ApplicationFormData, e.target.value)}
            placeholder="Enter your response..."
            maxLength={1000}
            rows={6}
            style={{
              width: '100%',
              padding: '10px',
              borderRadius: '4px',
              border: '1px solid #ccc',
              backgroundColor: theme.colors.background[0],
              color: theme.colors.textAndIcon[0],
              resize: 'vertical',
            }}
          />
          <Text size="sm" style={{ color: theme.colors.paragraph[0] }}>
            {formData[key as keyof ApplicationFormData]?.length}/1000 characters
          </Text>
        </Stack>
      ))}

      {error && (
        <Stack gap="md">
          <Text size="sm" style={{ color: theme.colors.notification[0] }}>
            {error}
          </Text>
          {error === ERROR_MESSAGES.NO_TOKEN && (
            <RootButton style1={false} secondary onClick={() => fetchAuthToken()}>
              Sign Message
            </RootButton>
          )}
        </Stack>
      )}

      <Stack style={{ flexDirection: 'row', gap: '15px', marginTop: '20px' }}>
        <RootButton style1={false} secondary expand onClick={() => navigate('/elections')}>
          {t('buttons.cancel', 'Cancel')}
        </RootButton>
        <RootButton
          style1={false}
          expand
          loading={isSubmitting}
          disabled={
            !address ||
            !isFormValid() ||
            isSubmitting ||
            submitted ||
            !token ||
            fillApplicationPeriodEnded
          }
          onClick={handleSubmitButton}
        >
          {t('buttons.submitApplication', 'Submit Application')}
        </RootButton>
      </Stack>
      <ModalSkeleton opened={opened} onClose={close} title="Accept Nomination">
        <ActionNomination
          close={close}
          action="accept"
          candidate={address as string}
          beforeAccept={handleSubmit}
        />
      </ModalSkeleton>
      <ModalSkeleton
        title="Election Application"
        opened={!!address && isNominee(address) && submitted}
        onClose={() => setSubmitted(false)}
      >
        <ConfirmationReady
          message="Your application has been edited"
          success={() => navigate(profilePath)}
          successMessage="Check Your Profile"
        />
      </ModalSkeleton>
    </BodyStack>
  )
}
Component.displayName = 'ApplicationForm'

export { Component }
